import { useState } from 'react';
import { Bell, Send, Filter, Eye, Info, AlertCircle, CheckCircle } from 'lucide-react';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { ConfirmDialog } from './ConfirmDialog';
import { useAdminModalZIndex } from './AdminModalZContext';
import { devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';


interface AdminInAppNotificationsProps {
  accessToken: string;
}

export function AdminInAppNotifications({ accessToken }: AdminInAppNotificationsProps) {
  const adminModalZ = useAdminModalZIndex();
  const [filters, setFilters] = useState({
    fylke: '',
    user_type: '',
    subscription: ''
  });
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState('admin_announcement');
  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [result, setResult] = useState<any>(null);
  
  // Confirm dialog state
  const [showSendConfirm, setShowSendConfirm] = useState(false);

  const fylker = [
    'Agder', 'Akershus', 'Buskerud', 'Finnmark', 'Innlandet',
    'Møre og Romsdal', 'Nordland', 'Oslo', 'Rogaland', 'Telemark',
    'Troms', 'Trøndelag', 'Vestfold', 'Vestland', 'Østfold'
  ];

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/preview-users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ filters })
        }
      );

      const data = await response.json();
      
      if (response.ok) {
        setPreviewCount(data.count);
        toast.success(`Forhåndsvisning: ${data.count} brukere vil motta varselet`);
      } else {
        toast.error(`Feil: ${data.error}`);
      }
    } catch (error) {
      devError('Error previewing users:', error);
      toast.error('Noe gikk galt ved forhåndsvisning');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSendClick = () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Tittel og melding er påkrevd');
      return;
    }
    setShowSendConfirm(true);
  };

  const handleSend = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/send-in-app-notification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            filters, 
            title, 
            message,
            type: notificationType,
            destination: destination || null
          })
        }
      );

      const data = await response.json();
      setResult(data);
      
      if (response.ok) {
        toast.success(`In-app varsler sendt! ${data.count} brukere mottok varselet.`);
        // Reset form
        setTitle('');
        setMessage('');
        setPreviewCount(null);
      } else {
        toast.error(`Feil: ${data.error}`);
      }
    } catch (error) {
      devError('Error sending in-app notifications:', error);
      toast.error('Noe gikk galt ved sending av varsler');
    } finally {
      setLoading(false);
      setShowSendConfirm(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-blue-900 mb-1">Om in-app varsler</h3>
          <p className="text-sm text-blue-800">
            In-app varsler vises i brukerens notification center (bell icon) og lagres permanent. 
            De sendes IKKE som push notifications til enhetene deres.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filtrer mottakere</h2>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          {/* County Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fylke
            </label>
            <select
              value={filters.fylke}
              onChange={(e) => setFilters({ ...filters, fylke: e.target.value })}
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Alle fylker</option>
              {fylker.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          {/* User Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brukertype
            </label>
            <select
              value={filters.user_type}
              onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Alle typer</option>
              <option value="talent">Talent</option>
              <option value="employer">Arbeidsgiver</option>
            </select>
          </div>

          {/* Subscription Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Abonnement
            </label>
            <select
              value={filters.subscription}
              onChange={(e) => setFilters({ ...filters, subscription: e.target.value })}
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Alle abonnementer</option>
              <option value="free">Gratis</option>
              <option value="plus">Talentfunn Pluss</option>
              <option value="employer">Arbeidsgiver Basis</option>
              <option value="employer_plus">Arbeidsgiver Pluss</option>
            </select>
          </div>
        </div>

        {/* Preview Button */}
        <button
          onClick={handlePreview}
          disabled={previewing}
          className="mt-4 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          {previewing ? 'Laster...' : 'Forhåndsvis antall mottakere'}
        </button>

        {previewCount !== null && (
          <div className="mt-3 flex items-center gap-2 text-purple-700 bg-purple-50 px-4 py-2 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{previewCount} brukere</span>
            <span className="text-sm">vil motta varselet</span>
          </div>
        )}
      </div>

      {/* Notification Content */}
      <div className="bg-white border-2 border-purple-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Varselinnhold</h2>
        </div>

        <div className="space-y-4">
          {/* Notification Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type varsel
            </label>
            <select
              value={notificationType}
              onChange={(e) => setNotificationType(e.target.value)}
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="admin_announcement">Systemmelding</option>
              <option value="profile_view">Profilvisning</option>
              <option value="premium_activated">Premium aktivert</option>
              <option value="profile_approved">Profil godkjent</option>
              <option value="content_warning">Innholdsadvarsel</option>
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tittel *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="F.eks: Ny funksjon lansert!"
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Melding *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Skriv meldingen som brukerne vil se..."
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          {/* Destination (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Destination (valgfritt)
            </label>
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full px-4 py-2 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Ingen (varselet er kun informativt)</option>
              <option value="home">Home</option>
              <option value="messages">Messages</option>
              <option value="profile">Profile</option>
              <option value="subscription">Subscription</option>
              <option value="gigs">Gigs</option>
              <option value="my-gigs">My Gigs</option>
              <option value="my-interests">My Interests</option>
              <option value="gig-interests">Gig Interests</option>
              <option value="favorites">Favorites</option>
              <option value="saved-searches">Saved Searches</option>
              <option value="settings">Settings</option>
              <option value="faq">FAQ</option>
              <option value="about">About</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Hvis du velger en destination, vil brukeren bli navigert dit når de trykker på varselet
            </p>
          </div>
        </div>
      </div>

      {/* Send Button */}
      <button
        onClick={handleSendClick}
        disabled={loading || !title.trim() || !message.trim()}
        className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Send className="w-5 h-5" />
        {loading ? 'Sender...' : 'Send in-app varsler'}
      </button>

      {/* Result */}
      {result && (
        <div className={`border-2 rounded-xl p-4 ${
          result.success 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center gap-2">
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <p className={result.success ? 'text-green-800' : 'text-red-800'}>
              {result.success 
                ? `Varsler sendt til ${result.count} brukere!`
                : `Feil: ${result.error}`
              }
            </p>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showSendConfirm}
        onClose={() => setShowSendConfirm(false)}
        onConfirm={handleSend}
        title="Send in-app varsler?"
        message={previewCount !== null 
          ? `Er du sikker på at du vil sende in-app varsel til ${previewCount} brukere?`
          : 'Er du sikker på at du vil sende in-app varsel til filtrerte brukere?'}
        confirmText="Send"
        cancelText="Avbryt"
        variant="warning"
        loading={loading}
        loadingText="Sender..."
        portalZIndex={adminModalZ}
      />
    </div>
  );
}
